export type CatalogMessages = Record<string, unknown>;

export type CatalogNamespaceLoader<
  Locale extends string,
  Namespace extends string,
> = (locale: Locale, namespace: Namespace) => Promise<CatalogMessages>;

export type LocaleCatalog<Locale extends string, Namespace extends string> = {
  ensureLocaleNamespaces(
    locale: Locale,
    namespaces: readonly Namespace[],
  ): Promise<void>;
  getLocaleMessage(locale: Locale): CatalogMessages;
};

function isPlainObject(v: unknown): v is CatalogMessages {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function deepMerge(
  target: CatalogMessages,
  source: CatalogMessages,
): CatalogMessages {
  for (const [k, v] of Object.entries(source)) {
    const existing = target[k];
    if (isPlainObject(existing) && isPlainObject(v)) {
      target[k] = deepMerge({ ...existing }, v);
    } else {
      target[k] = v;
    }
  }
  return target;
}

export function createLocaleCatalog<
  Locale extends string,
  Namespace extends string,
>(
  loadNamespace: CatalogNamespaceLoader<Locale, Namespace>,
  fallbackLocale: Locale,
): LocaleCatalog<Locale, Namespace> {
  const loadedNamespaces = new Map<Locale, Set<Namespace>>();
  const localeMessages = new Map<Locale, CatalogMessages>();

  function getLoadedSet(locale: Locale): Set<Namespace> {
    let set = loadedNamespaces.get(locale);
    if (!set) {
      set = new Set();
      loadedNamespaces.set(locale, set);
    }
    return set;
  }

  async function ensureLocaleNamespaces(
    locale: Locale,
    namespaces: readonly Namespace[],
  ): Promise<void> {
    if (locale !== fallbackLocale) {
      await ensureLocaleNamespaces(fallbackLocale, namespaces);
    }

    const loadedSet = getLoadedSet(locale);
    const toLoad = namespaces.filter((ns) => !loadedSet.has(ns));
    if (toLoad.length === 0) {
      return;
    }

    let merged: CatalogMessages = { ...localeMessages.get(locale) };

    for (const namespace of toLoad) {
      const chunk = await loadNamespace(locale, namespace);
      if (Object.keys(chunk).length > 0) {
        merged = deepMerge(merged, chunk);
      }
      loadedSet.add(namespace);
    }

    localeMessages.set(locale, merged);
  }

  function getLocaleMessage(locale: Locale): CatalogMessages {
    return localeMessages.get(locale) ?? {};
  }

  return { ensureLocaleNamespaces, getLocaleMessage };
}
